import { useState,useEffect,useRef } from 'react';
import MavisQualificationEvidence from './MavisQualificationEvidence';
import { BrainCircuit } from 'lucide-react';
import type { MeddpiccData, MeddpiccKey, MeddpiccScore, MeddpiccField } from '../../types/mavis';
import { MEDDPICC_KEY_LABELS, MEDDPICC_SCORE_LABELS } from '../../types/mavis';

const MEDDPICC_KEYS: MeddpiccKey[] = [
  'metrics', 'economic_buyer', 'decision_criteria', 'decision_process',
  'partners', 'paper_process', 'implicated_pain', 'champion', 'competition',
];

// Score chip styles — active (selected) and inactive (available)
const SCORE_ACTIVE: Record<MeddpiccScore, string> = {
  0: 'bg-app-canvas text-app-muted ring-1 ring-app-line-strong',
  1: 'bg-red-100 text-red-700 ring-1 ring-red-200',
  2: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200',
  3: 'bg-forest-100 text-forest-700 ring-1 ring-forest-200',
};
const SCORE_INACTIVE =
  'bg-app-canvas text-app-muted hover:bg-app-canvas hover:text-app-muted transition-colors';

const SCORE_LABELS_SHORT: Record<MeddpiccScore, string> = {
  0: '—',
  1: MEDDPICC_SCORE_LABELS[1],
  2: 'Dev',
  3: MEDDPICC_SCORE_LABELS[3],
};

const textArea =
  'w-full bg-transparent border-0 focus:outline-none resize-none leading-relaxed placeholder:text-app-muted';

interface Props {
  data: MeddpiccData;
  saving: boolean;
  onSave: (updated: MeddpiccData) => void;
}

export default function MavisMeddpiccPanel({ data, saving, onSave }: Props) {
  const [draft, setDraft] = useState<MeddpiccData>(data);
  const dirty=useRef(new Set<MeddpiccKey>());
  useEffect(()=>setDraft(prev=>({...data,...Object.fromEntries([...dirty.current].map(k=>[k,prev[k]]))})),[data]);

  const update = (key: MeddpiccKey, patch: Partial<MeddpiccField>) => {
    dirty.current.add(key);setDraft(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const handleScore = (key: MeddpiccKey, score: MeddpiccScore) => {
    const updated = { ...draft, [key]: { ...draft[key], score } };
    setDraft(updated);
    onSave({...data,...Object.fromEntries([...dirty.current,key].map(k=>[k,updated[k]]))});dirty.current.delete(key);
  };

  const handleBlur = () => {
    onSave({...data,...Object.fromEntries([...dirty.current].map(k=>[k,draft[k]]))});dirty.current.clear();
  };

  return (
    <div className="space-y-1">
      {/* Field grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {MEDDPICC_KEYS.map(k => (
          <div key={k} className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
            {/* Card header: label + score buttons */}
            <div className="px-5 py-3 border-b border-app-line flex items-center justify-between gap-3">
              <h4 className="text-sm font-semibold text-app-ink truncate">
                {MEDDPICC_KEY_LABELS[k]}
              </h4>
              <div className="flex items-center gap-1 flex-shrink-0">
                {([0, 1, 2, 3] as MeddpiccScore[]).map(s => (
                  <button
                    key={s}
                    onClick={() => handleScore(k, s)}
                    disabled={saving}
                    className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                      (draft[k]?.score??0) === s ? SCORE_ACTIVE[s] : SCORE_INACTIVE
                    }`}
                  >
                    {SCORE_LABELS_SHORT[s]}
                  </button>
                ))}
              </div>
            </div>

            {/* Card body: value + notes */}
            <div className="px-5 py-4 space-y-3">
              <MavisQualificationEvidence field={data[k]}/>
              <div>
                <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-1">
                  What we know
                </p>
                <textarea
                  value={(draft[k]?.value??'')}
                  onChange={e => update(k, { value: e.target.value })}
                  onBlur={handleBlur}
                  rows={3}
                  className={`${textArea} text-sm text-app-ink`}
                  placeholder="Document your knowledge of this area…"
                />
              </div>
              <div className="border-t border-app-line pt-2.5">
                <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-1">
                  Notes
                </p>
                <textarea
                  value={(draft[k]?.notes??'')}
                  onChange={e => update(k, { notes: e.target.value })}
                  onBlur={handleBlur}
                  rows={2}
                  className={`${textArea} text-xs text-app-muted`}
                  placeholder="Action items, open questions, sources…"
                />
              </div>

              {/* AI assist placeholder */}
              <button
                disabled
                className="flex items-center gap-1.5 text-[11px] text-app-muted cursor-not-allowed mt-1"
                title="MAVIS AI assist — coming soon"
              >
                <BrainCircuit className="w-3.5 h-3.5" />
                Ask MAVIS
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
